import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, Mail, ShieldCheck } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const AdminLoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(false);
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // If already authenticated, go straight to the dashboard.
    fetch(`${API_URL}/api/admin/me`, { credentials: 'include' })
      .then((r) => { if (r.ok) navigate('/admin/dashboard'); })
      .catch(() => {});
    // Detect first-run (no admin account yet).
    fetch(`${API_URL}/api/admin/auth-status`)
      .then((r) => r.json())
      .then((d) => setNeedsSetup(!d.setup_complete))
      .catch(() => {});
  }, [API_URL, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });
      if (res.ok) {
        navigate('/admin/dashboard');
      } else if (res.status === 429) {
        setError('Too many failed attempts. Please try again in a few minutes.');
      } else if (res.status === 401) {
        setError('Invalid email or password.');
      } else {
        setError('Server error. Please try again later.');
      }
    } catch {
      setError('Unable to connect to server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] to-[#E2E8F0] flex items-center justify-center px-4">
      <Helmet>
        <title>Admin Login | TCUBE FITNESS™</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#1E88E5] rounded-full mb-4">
            <ShieldCheck className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-[#1F2933]">Admin Dashboard</h1>
          <p className="text-[#64748B] mt-2">TCUBE FITNESS Consultation Management</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <form onSubmit={handleLogin} data-testid="admin-login-form">
            <div className="mb-5">
              <label htmlFor="email" className="block text-sm font-medium text-[#1F2933] mb-2">Email Address</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-[#94A3B8]" />
                </div>
                <input
                  type="email"
                  id="email"
                  data-testid="admin-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full pl-10 pr-4 py-3 border border-[#CBD5E1] rounded-xl focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent outline-none transition-all"
                  required
                />
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor="password" className="block text-sm font-medium text-[#1F2933] mb-2">Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-[#94A3B8]" />
                </div>
                <input
                  type={showPw ? 'text' : 'password'}
                  id="password"
                  data-testid="admin-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full pl-10 pr-12 py-3 border border-[#CBD5E1] rounded-xl focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent outline-none transition-all"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  data-testid="toggle-password-visibility"
                >
                  {showPw ? <EyeOff className="h-5 w-5 text-[#94A3B8]" /> : <Eye className="h-5 w-5 text-[#94A3B8]" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !email || !password}
              data-testid="login-submit-btn"
              className="w-full py-3 px-4 bg-[#1E88E5] text-white font-semibold rounded-xl hover:bg-[#1976D2] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          {needsSetup && (
            <div className="mt-6 p-4 bg-[#EFF6FF] border border-[#BFDBFE] rounded-lg text-sm text-[#1E3A8A]" data-testid="setup-notice">
              No admin account exists yet.{' '}
              <button
                onClick={() => navigate('/admin/setup')}
                className="font-semibold text-[#1E88E5] hover:underline"
                data-testid="go-to-setup-btn"
              >
                Set up the first administrator
              </button>
              .
            </div>
          )}
        </div>

        <div className="text-center mt-6">
          <a href="/" className="text-[#1E88E5] hover:text-[#1976D2] text-sm font-medium transition-colors">
            Back to Website
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
